import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Check } from "lucide-react";
import ShowcaseManager from "@/components/ShowcaseManager";
import ShowcaseSetup from "@/components/ShowcaseSetup";
import BrokeragePageEditor from "@/components/BrokeragePageEditor";
import LeadRouting from "@/components/LeadRouting";
import BrokerageProRequests from "@/components/BrokerageProRequests";
import SettingsRow from "@/components/SettingsRow";
import AppLink from "@/components/AppLink";
import { useLeadRouting, useManagedBrokerage, useProRequests } from "@/hooks/useBrokerageManage";
import { useShowcase } from "@/hooks/useShowcase";
import { usePackage } from "@/hooks/usePlans";
import { hostLabelOf } from "@/components/AgentWebsiteRow/agentWebsiteModel";
import SelectDropDown from "@/components/Common/SelectDropDown";
import { PICKER_STYLES } from "@/helpers/pickerStyles";
import { checklistOf, initialRowOf, planActiveOf, rowStatusOf } from "./hubModel";
import { plansPathFor } from "@/helpers/brokeragePlanPath";
import "./BrokerageStudio.scss";

export { ABOUT_MAX, aboutErrorOf, draftOf, isDirty } from "@/components/BrokeragePageEditor/model";

export const notManagedNotice = (brokerages, requestedId, name) =>
  requestedId && brokerages.length && !brokerages.some((item) => item.id === requestedId)
    ? `You don't manage that brokerage page, so we opened ${name}.`
    : "";

export { plansPathFor };

export const pageHrefOf = (data) =>
  data?.pagePath || (data?.network ? `/agents?b=${encodeURIComponent(data.name)}&n=${data.network}` : "");

const BrokerageSwitch = ({ brokerages, selectedId, onSelect }) => {
  if (brokerages.length < 2) return null;
  const options = brokerages.map((item) => ({
    value: item.id,
    label: item.networkName ? `${item.name} (${item.networkName})` : item.name,
  }));
  return (
    <div className="brokerage-studio__switch">
      <span>Brokerage</span>
      <SelectDropDown
        id="brokerage-switch"
        classNamePrefix="select"
        options={options}
        value={options.find((option) => option.value === selectedId)}
        styles={PICKER_STYLES}
        errorClass="d-none"
        onChange={(option) => option && onSelect(Number(option.value))}
      />
    </div>
  );
};

export const initialsOf = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter((word) => /^[A-Za-z0-9]/.test(word))
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("");

const Checklist = ({ items, onOpen }) => {
  const done = items.filter((item) => item.done).length;
  const next = items.find((item) => !item.done);
  return (
    <div className="brokerage-hub__setup">
      <div className="brokerage-hub__setup-head">
        <span className="brokerage-hub__setup-title">Set up your brokerage</span>
        <span className="brokerage-hub__setup-count">
          {done} of {items.length} done
        </span>
      </div>
      <div
        className="brokerage-hub__progress"
        role="progressbar"
        aria-label="Brokerage setup"
        aria-valuemin={0}
        aria-valuemax={items.length}
        aria-valuenow={done}
      >
        <span style={{ transform: `scaleX(${items.length ? done / items.length : 0})` }} />
      </div>
      <ol className="brokerage-hub__checklist" aria-label="Set up your brokerage">
        {items.map((item, index) => (
          <li
            key={item.key}
            className={`brokerage-hub__check${item.done ? " is-done" : ""}${item === next ? " is-next" : ""}`}
            style={{ "--i": index }}
          >
            <span className="brokerage-hub__check-dot" aria-hidden="true">
              {item.done ? <Check size={12} strokeWidth={3} /> : null}
            </span>
            <span className="brokerage-hub__check-label">{item.label}</span>
            {item.done ? (
              <span className="brokerage-hub__check-state">Done</span>
            ) : (
              <button type="button" className="brokerage-hub__check-go" onClick={() => onOpen(item.key)}>
                {item.key === "leads" ? "How it works" : "Set up"}
              </button>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
};

const copyLink = async (href) => {
  try {
    await navigator.clipboard.writeText(new URL(href, window.location.origin).toString());
    toast.success("Link copied.");
  } catch {
    toast.error("Copy did not work. Open the page and copy the address.");
  }
};

const BrokerageStudio = ({ brokerages, selectedId, onSelect, requestedId = null }) => {
  const navigate = useNavigate();
  const query = useManagedBrokerage(selectedId);
  const data = query.data;
  const showcase = useShowcase(selectedId).data?.showcase;
  const routing = useLeadRouting(selectedId).data;
  const requestsQuery = useProRequests(selectedId);
  const requests = requestsQuery.data?.requests || [];
  const requestsFailed = Boolean(requestsQuery.isError);
  const brokeragePlan = usePackage("brokerage").pkg;
  const [open, setOpen] = useState(() => initialRowOf(window.location.search));
  const rowsRef = useRef(null);
  const current = brokerages.find((item) => item.id === selectedId);
  const name = data?.name || current?.name || "your brokerage";
  const notice = notManagedNotice(brokerages, requestedId, current?.name || name);

  useEffect(() => {
    if (open) rowsRef.current?.querySelector(".settings-row.is-open")?.scrollIntoView({ block: "start" });
  }, []);

  if (!data) {
    return (
      <div className="brokerage-studio-shell">
        <BrokerageSwitch brokerages={brokerages} selectedId={selectedId} onSelect={onSelect} />
        {query.isError ? (
          <div className="brokerage-studio__state">
            Your brokerage did not load.{" "}
            <button type="button" className="btn btn-link p-0 align-baseline" onClick={() => query.refetch()}>
              Try again
            </button>
          </div>
        ) : (
          <div className="brokerage-hub__skeleton" aria-busy="true" aria-label="Loading your brokerage">
            <span />
            <span />
            <span />
          </div>
        )}
      </div>
    );
  }

  const planActive = planActiveOf(data, brokeragePlan);
  const host = hostLabelOf(data.settings?.website);
  const checklist = checklistOf({ data, showcase });
  const setupDone = checklist.every((item) => item.done);
  const pageHref = pageHrefOf(data);
  const facts = { data, showcase, planActive, requests, requestsFailed, routing };
  const toggle = (key) => () => setOpen(open === key ? null : key);
  const openRow = (key) => {
    setOpen(key);
    requestAnimationFrame(() =>
      rowsRef.current?.querySelector(`[data-row="${key}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" }),
    );
  };
  const seePlan = (seats = []) => navigate(plansPathFor(selectedId, seats));

  const row = (key, title, children, actionLabel = "Edit") => (
    <div data-row={key}>
      <SettingsRow
        title={title}
        status={rowStatusOf(key, facts)}
        actionLabel={actionLabel}
        open={open === key}
        onToggle={toggle(key)}
      >
        {children}
      </SettingsRow>
    </div>
  );

  return (
    <div className="brokerage-studio-shell brokerage-hub">
      {notice ? (
        <p className="brokerage-studio-shell__notice" role="status">
          {notice}
        </p>
      ) : null}
      <BrokerageSwitch brokerages={brokerages} selectedId={selectedId} onSelect={onSelect} />

      <section className="content-block brokerage-hub__head">
        <div className="brokerage-hub__identity">
          {data.settings?.logoUrl || data.brandLogoUrl ? (
            <img className="brokerage-hub__logo" src={data.settings?.logoUrl || data.brandLogoUrl} alt="" />
          ) : (
            <span className="brokerage-hub__logo brokerage-hub__logo--mark" aria-hidden="true">
              {initialsOf(data.name)}
            </span>
          )}
          <div className="brokerage-hub__names">
            <h3 className="brokerage-hub__name">{data.name}</h3>
            {pageHref ? (
              <p className="brokerage-hub__links">
                <AppLink href={pageHref} target="_blank" rel="noopener noreferrer">
                  View page
                </AppLink>
                <button type="button" className="brokerage-hub__copy" onClick={() => copyLink(pageHref)}>
                  Copy link
                </button>
              </p>
            ) : null}
          </div>
        </div>
        <button type="button" className="btn btn-primary brokerage-hub__primary" onClick={() => openRow("website")}>
          {showcase?.checklist?.live ? "Manage your website feed" : `Put your buyers on ${host || "your website"}`}
        </button>
        {setupDone ? null : <Checklist items={checklist} onOpen={openRow} />}
      </section>

      <section className="content-block brokerage-hub__rows" ref={rowsRef}>
        {row("page", "Page", <BrokeragePageEditor key={selectedId} selectedId={selectedId} part="page" />)}
        {row("agents", "Agents and teams", <BrokeragePageEditor key={selectedId} selectedId={selectedId} part="agents" />)}
        {row(
          "website",
          "On your website",
          planActive ? (
            <>
              <ShowcaseSetup key={selectedId} brokerageId={selectedId} brokerageName={name} />
              <ShowcaseManager brokerageId={selectedId} />
            </>
          ) : (
            <>
              <ShowcaseSetup key={selectedId} brokerageId={selectedId} brokerageName={name} previewOnly />
              <div className="brokerage-hub__offer">
                <p>
                  Your agents&apos; public Buyer Want&#8209;Listings on {host || "your website"}, with every lead sent to the
                  right agent. Part of the brokerage&nbsp;plan.
                </p>
                <button type="button" className="btn btn-primary" onClick={() => seePlan()}>
                  See the brokerage plan
                </button>
              </div>
            </>
          ),
          showcase?.checklist?.live ? "Manage" : "Set up",
        )}
        {row("leads", "Website leads", <LeadRouting brokerageId={selectedId} onSeePlan={() => seePlan()} />, "Change")}
        {row(
          "pro",
          "Pro for your agents",
          <BrokerageProRequests
            brokerageId={selectedId}
            requests={requests}
            failed={requestsFailed}
            onCover={seePlan}
            planActive={planActive}
            seatUserIds={data?.plan?.seatUserIds || []}
          />,
          requests.length ? "Review" : "Open",
        )}
        {row("editors", "Who can edit", <BrokeragePageEditor key={selectedId} selectedId={selectedId} part="editors" />, "Manage")}
      </section>
    </div>
  );
};

export default BrokerageStudio;
